/**
 * analyticsExport.js
 *
 * Builds and downloads a two-sheet Analytics Excel report using ExcelJS.
 *
 * Sheet 1 — Lista de Conversaciones
 *   Source: rows returned by GET /analytics/export-report/clients
 *   Fields: nombre, telefono (senderId), canal (name), agente (name),
 *           etiquetas (tag names), fechaCreacion, ultimaActualizacion.
 *   Dates are pre-formatted in local time by the backend using the caller's
 *   timezone offset (timezoneOffset param).
 *
 * Sheet 2 — Métricas de Agentes
 *   Source: ClickHouse user-analytics data fetched in AdvancedAnalyticsDashboard,
 *   identical to the data shown in the "Usuarios" tab widget.
 */

import * as Excel from "exceljs";
import * as FileSaver from "file-saver";

// ─── Style helpers ────────────────────────────────────────────────────────────

const HEADER_FILL = {
  type: "pattern",
  pattern: "solid",
  fgColor: { argb: "FF6366F1" }, // indigo-500
};

const HEADER_FONT = { bold: true, color: { argb: "FFFFFFFF" }, size: 11 };
const BORDER_THIN = { style: "thin", color: { argb: "FFE5E7EB" } };
const CELL_BORDER = {
  top: BORDER_THIN,
  left: BORDER_THIN,
  bottom: BORDER_THIN,
  right: BORDER_THIN,
};

function styleHeaderRow(row) {
  row.eachCell((cell) => {
    // eslint-disable-next-line no-param-reassign
    cell.fill = HEADER_FILL;
    // eslint-disable-next-line no-param-reassign
    cell.font = HEADER_FONT;
    // eslint-disable-next-line no-param-reassign
    cell.border = CELL_BORDER;
    // eslint-disable-next-line no-param-reassign
    cell.alignment = { vertical: "middle", horizontal: "center" };
  });
  row.height = 22;
}

function styleDataRow(row) {
  row.eachCell({ includeEmpty: true }, (cell) => {
    // eslint-disable-next-line no-param-reassign
    cell.border = CELL_BORDER;
    // eslint-disable-next-line no-param-reassign
    cell.alignment = { vertical: "middle" };
  });
}

// ─── Sheet builders ───────────────────────────────────────────────────────────

/**
 * Sheet 1: Lista de Conversaciones.
 *
 * Expected row shape (from ExportReportService):
 *   { nombre, telefono, canal, agente, etiquetas, fechaCreacion, ultimaActualizacion }
 *
 * - "Teléfono"   ← senderId (WhatsApp E.164 number)
 * - "Canal"      ← channel name (resolved from MongoDB Channel collection)
 * - "Etiquetas"  ← tag names joined by ", " (resolved from Tag ObjectIds)
 * - Dates are already in the user's local time (formatted by the backend).
 * - "Plataforma" column has been removed per spec.
 */
function buildConversationsSheet(wb, rows) {
  const ws = wb.addWorksheet("Lista de Conversaciones");

  ws.columns = [
    { header: "Nombre",               key: "nombre",              width: 28 },
    { header: "Teléfono",             key: "telefono",            width: 20 },
    { header: "Canal",                key: "canal",               width: 30 },
    { header: "Agente",               key: "agente",              width: 28 },
    { header: "Etiquetas",            key: "etiquetas",           width: 28 },
    { header: "Fecha de Creación",    key: "fechaCreacion",       width: 22 },
    { header: "Última Actualización", key: "ultimaActualizacion", width: 22 },
  ];

  styleHeaderRow(ws.getRow(1));

  rows.forEach((row) => {
    const added = ws.addRow(row);
    styleDataRow(added);
  });

  ws.views = [{ state: "frozen", ySplit: 1 }];
}

/**
 * Sheet 2: Métricas de Agentes.
 *
 * Data is identical to what the "Usuarios" tab widget displays:
 *  - topUsersData  → derived from daily_user_stats ClickHouse view
 *  - assignedMap   → from /analytics/dashboard/assigned-conversations
 *  - agentRatingsMap → from /analytics/clickhouse/view/agent_performance_daily
 *
 * @param {Excel.Workbook} wb
 * @param {object[]} usersData         - topUsersData items: { userId, userName,
 *                                        outgoingMessages, finishedClients,
 *                                        avgResponseTime, responsesAfterBot }
 * @param {Map<string,number>} assignedMap      - agentId → assigned conversations
 * @param {Map<string,number>} agentRatingsMap  - agentId → rating % (0-100)
 */
function buildAgentsSheet(wb, usersData, assignedMap, agentRatingsMap) {
  const ws = wb.addWorksheet("Métricas de Agentes");

  ws.columns = [
    { header: "Agente",                       key: "userName",         width: 30 },
    { header: "Conv. Asignadas",              key: "assigned",         width: 18 },
    { header: "Mensajes Enviados",            key: "outgoingMessages", width: 18 },
    { header: "Conv. Finalizadas",            key: "finishedClients",  width: 18 },
    { header: "T. Prom. Respuesta (s)",       key: "avgResponseTime",  width: 24 },
    { header: "Resp. tras Bot",               key: "responsesAfterBot",width: 16 },
    { header: "Eficiencia (%)",               key: "efficiency",       width: 16 },
    { header: "Calificación (%)",             key: "rating",           width: 16 },
  ];

  styleHeaderRow(ws.getRow(1));

  usersData.forEach((user) => {
    const assigned = assignedMap?.get(user.userId) ?? 0;
    const ratingPct = agentRatingsMap?.get(user.userId);
    const efficiencyPct =
      user.outgoingMessages > 0
        ? ((user.finishedClients / user.outgoingMessages) * 100).toFixed(1)
        : "0.0";

    const added = ws.addRow({
      userName:          user.userName,
      assigned,
      outgoingMessages:  user.outgoingMessages,
      finishedClients:   user.finishedClients,
      avgResponseTime:   Number((user.avgResponseTime ?? 0).toFixed(1)),
      responsesAfterBot: user.responsesAfterBot,
      efficiency:        Number(efficiencyPct),
      rating:            ratingPct != null ? Number(ratingPct.toFixed(1)) : "",
    });
    styleDataRow(added);
  });

  ws.views = [{ state: "frozen", ySplit: 1 }];
}

// ─── Public API ───────────────────────────────────────────────────────────────

/**
 * Generates and triggers download of the Analytics Excel report.
 *
 * @param {object}               opts
 * @param {string}               opts.startDate          - "YYYY-MM-DD"
 * @param {string}               opts.endDate            - "YYYY-MM-DD"
 * @param {object[]}             opts.conversationRows   - from ExportReportService
 * @param {object[]}             opts.usersData          - topUsersData (identical to widget)
 * @param {Map<string,number>}   opts.assignedMap        - agentId → assigned conversations
 * @param {Map<string,number>}   opts.agentRatingsMap    - agentId → rating %
 */
export async function downloadAnalyticsReport({
  startDate,
  endDate,
  conversationRows,
  usersData,
  assignedMap,
  agentRatingsMap,
}) {
  const wb = new Excel.Workbook();
  wb.creator = "iVentas Analytics";
  wb.created = new Date();

  buildConversationsSheet(wb, conversationRows);
  buildAgentsSheet(wb, usersData || [], assignedMap, agentRatingsMap);

  const buffer = await wb.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8",
  });

  FileSaver.saveAs(blob, `Reporte_Analytics_${startDate}_${endDate}.xlsx`);
}
